import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { supabase } from "../supabaseClient";
import PageSeo from "../components/PageSeo";

export default function Profile() {
  const { user, updateUserProfile } = useApp();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 900 : false
  );
  const [form, setForm] = useState({
    name: "",
    username: "",
    bio: "",
    city: "",
    state: "",
    bike: "",
    bikes_owned: [],
    phone: "",
    experience: "Intermediate",
    preferred_ride: "Adventure",
    riding_style: "commuter",
    instagram: "",
    youtube: "",
    is_public: true,
    role: "rider",
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [bikesInput, setBikesInput] = useState("");
  const initialUsername = useRef("");

  const ridingStyles = [
    { value: "commuter", label: "Commuter" },
    { value: "tourer", label: "Tourer" },
    { value: "offroad", label: "Off-road" },
    { value: "track", label: "Track" },
    { value: "adventure", label: "Adventure" },
  ];
  const ridingStyleLabels = ridingStyles.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      username: user.username || "",
      bio: user.bio || "",
      city: user.city || "",
      state: user.state || "",
      bike: user.bike || "",
      bikes_owned: Array.isArray(user.bikes_owned) ? user.bikes_owned : [],
      phone: user.phone ? user.phone.replace(/^\+91/, "") : "",
      experience: user.experience || "Intermediate",
      preferred_ride: user.preferred_ride || "Adventure",
      riding_style: user.riding_style || "commuter",
      instagram: user.instagram || "",
      youtube: user.youtube || "",
      is_public: typeof user.is_public === "boolean" ? user.is_public : true,
      role: user.role || "rider",
    });
    initialUsername.current = user.username || "";
    setUsernameError("");
    setBikesInput("");
  }, [user]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const profileCompletion = useMemo(() => {
    const checks = [
      Boolean(form.name.trim()),
      Boolean(form.username.trim()),
      Boolean(form.city.trim()),
      Boolean(form.state.trim()),
      Boolean(form.bike.trim()),
      Boolean(form.phone.trim() || user?.email),
      Boolean(form.experience),
      Boolean(form.preferred_ride),
      Boolean(form.riding_style),
      Boolean(form.bio.trim()),
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [form, user]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleChange = (field, value) => {
    const nextValue =
      field === "username"
        ? value
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, "")
            .slice(0, 30)
        : field === "bio"
        ? value.slice(0, 160)
        : value;

    setForm((prev) => ({ ...prev, [field]: nextValue }));
    if (field === "username" && usernameError) {
      setUsernameError("");
    }
  };

  const handleUsernameBlur = async () => {
    const candidate = form.username.trim().toLowerCase();

    if (!candidate) {
      setUsernameError("Username is required.");
      return;
    }

    if (!/^[a-z0-9_]{3,30}$/.test(candidate)) {
      setUsernameError("Use 3-30 lowercase letters, numbers, or underscores.");
      return;
    }

    if (candidate === initialUsername.current.toLowerCase()) {
      setUsernameError("");
      return;
    }

    try {
      setUsernameChecking(true);
      const { data, error: checkError } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", candidate)
        .neq("id", user.id)
        .maybeSingle();

      if (checkError && checkError.code !== "PGRST116") {
        console.error("Username check failed", checkError);
        setUsernameError("Could not verify username. Try again.");
        return;
      }

      if (data) {
        setUsernameError("This username is already taken.");
      } else {
        setUsernameError("");
      }
    } finally {
      setUsernameChecking(false);
    }
  };

  const handleBikesAdd = () => {
    const trimmed = bikesInput.trim();
    if (!trimmed) return;
    setForm((prev) =>
      prev.bikes_owned.includes(trimmed)
        ? prev
        : { ...prev, bikes_owned: [...prev.bikes_owned, trimmed] }
    );
    setBikesInput("");
  };

  const handleBikesKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleBikesAdd();
    }
    if (event.key === "Backspace" && !bikesInput && form.bikes_owned.length) {
      event.preventDefault();
      setForm((prev) => ({
        ...prev,
        bikes_owned: prev.bikes_owned.slice(0, prev.bikes_owned.length - 1),
      }));
    }
  };

  const handleBikeRemove = (target) => {
    setForm((prev) => ({
      ...prev,
      bikes_owned: prev.bikes_owned.filter((bike) => bike !== target),
    }));
  };

  const togglePrivacy = () => {
    setForm((prev) => ({ ...prev, is_public: !prev.is_public }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }

    if (!form.username.trim()) {
      setError("Username is required.");
      return;
    }

    if (usernameError) {
      setError(usernameError);
      return;
    }

    if (usernameChecking) {
      setError("Please wait for username validation to finish.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    const payload = {
      name: form.name.trim(),
      username: form.username.trim().toLowerCase(),
      bio: form.bio.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      bike: form.bike.trim(),
      phone: form.phone.trim() ? `+91${form.phone.trim()}` : null,
      experience: form.experience,
      preferred_ride: form.preferred_ride,
      riding_style: form.riding_style,
      instagram: form.instagram.trim(),
      youtube: form.youtube.trim(),
      is_public: form.is_public,
      bikes_owned: form.bikes_owned,
      role: form.role,
    };

    const result = await updateUserProfile(payload);

    if (result.success) {
      await supabase.auth.updateUser({ data: {
        name: payload.name,
        city: payload.city,
        state: payload.state,
        bike: payload.bike,
        instagram: payload.instagram,
        youtube: payload.youtube,
        riding_style: payload.riding_style,
      }});
    }

    if (result.success) {
      setMessage("Profile updated successfully.");
      initialUsername.current = form.username.trim().toLowerCase();
    } else {
      setError(result.error || "Profile could not be updated.");
    }

    setSaving(false);
  };

  return (
    <div style={{ padding: isMobile ? "12px 0 0" : "32px 36px", maxWidth: 980, width: "100%" }}>
      <PageSeo
        title="Your Rider Profile | MotoHub India"
        description="Manage your MotoHub India rider profile, update your city, bike, and riding preferences, and keep your premium experience personalized."
        path="/profile"
        noindex
      />

      <div
        className="fade-up"
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 340px) minmax(320px, 1fr)",
          gap: 20,
        }}
      >
        <div style={sideCardStyle}>
          <div style={avatarStyle}>{(user.avatar_url || user.name?.slice(0, 2) || "MB").toUpperCase()}</div>
          <h1 style={{ marginBottom: 2 }}>{user.name}</h1>
          <div style={{ color: "var(--text2)", marginBottom: 12 }}>@{user.username || "yourhandle"}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
            <span style={badgeStyle}>{ridingStyleLabels[user.riding_style] || "Rider"}</span>
            <span style={badgeOutlineStyle}>{user.is_public ? "Public" : "Private"}</span>
          </div>
          {user.bio ? (
            <div style={{ color: "var(--text2)", lineHeight: 1.6, marginBottom: 18 }}>{user.bio}</div>
          ) : null}

          <div style={progressWrapStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text2)", marginBottom: 8 }}>
              <span>Profile completion</span>
              <span>{profileCompletion}%</span>
            </div>
            <div style={progressBarTrackStyle}>
              <div style={{ ...progressBarFillStyle, width: `${profileCompletion}%` }} />
            </div>
          </div>

          <div style={{ display: "grid", gap: 12, marginTop: 20 }}>
            <div style={infoRowStyle}>
              <span style={infoLabelStyle}>City</span>
              <span>{user.city || "Add your city"}</span>
            </div>
            <div style={infoRowStyle}>
              <span style={infoLabelStyle}>State</span>
              <span>{user.state || "Add your state"}</span>
            </div>
            <div style={infoRowStyle}>
              <span style={infoLabelStyle}>Bike</span>
              <span>{user.bike || "Add your bike"}</span>
            </div>
            <div style={infoRowStyle}>
              <span style={infoLabelStyle}>Phone</span>
              <span>{user.phone || "Optional"}</span>
            </div>
          </div>

          {Array.isArray(user.bikes_owned) && user.bikes_owned.length ? (
            <div style={{ marginTop: 20 }}>
              <div style={{ ...infoLabelStyle, marginBottom: 10 }}>Bikes Owned</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {user.bikes_owned.map((bike) => (
                  <span key={bike} style={profileBikeChipStyle}>
                    {bike}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <Link to="/" style={homeLinkStyle}>Back to dashboard</Link>
        </div>

        <form onSubmit={handleSubmit} style={formCardStyle}>
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 12, color: "var(--brand)", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 }}>
              Rider Settings
            </div>
            <h2 style={{ marginBottom: 8 }}>Edit your profile</h2>
            <p style={{ color: "var(--text2)", lineHeight: 1.7 }}>
              Keep your rider details up to date so MotoHub India can personalize routes, rides, and premium tools.
            </p>
          </div>

          <div style={formGridStyle}>
            <div>
              <label style={labelStyle}>Full name</label>
              <input value={form.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="Full name" />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input
                value={form.username}
                onChange={(e) => handleChange("username", e.target.value)}
                onBlur={handleUsernameBlur}
                placeholder="yourhandle"
              />
              {usernameChecking ? (
                <div style={helperStyle}>Checking availability…</div>
              ) : usernameError ? (
                <div style={errorStyle}>{usernameError}</div>
              ) : null}
            </div>
            <div>
              <label style={labelStyle}>City</label>
              <input value={form.city} onChange={(e) => handleChange("city", e.target.value)} placeholder="Pune, Mumbai, Bengaluru" />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <input value={form.state} onChange={(e) => handleChange("state", e.target.value)} placeholder="Maharashtra" />
            </div>
            <div>
              <label style={labelStyle}>Bike</label>
              <input value={form.bike} onChange={(e) => handleChange("bike", e.target.value)} placeholder="RE Himalayan 450" />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={form.phone} onChange={(e) => handleChange("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="9876543210" />
            </div>
            <div>
              <label style={labelStyle}>Experience</label>
              <select value={form.experience} onChange={(e) => handleChange("experience", e.target.value)}>
                <option>Beginner</option>
                <option>Intermediate</option>
                <option>Advanced</option>
                <option>Touring Expert</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Preferred ride</label>
              <select value={form.preferred_ride} onChange={(e) => handleChange("preferred_ride", e.target.value)}>
                <option>Adventure</option>
                <option>Weekend</option>
                <option>City Commute</option>
                <option>Touring</option>
                <option>Group Ride</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Riding style</label>
              <select value={form.riding_style} onChange={(e) => handleChange("riding_style", e.target.value)}>
                {ridingStyles.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Profile type</label>
              <select value={form.role} onChange={(e) => handleChange("role", e.target.value)}>
                <option value="rider">Rider</option>
                <option value="mechanic">Mechanic / Garage Owner</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Instagram URL</label>
              <input value={form.instagram} onChange={(e) => handleChange("instagram", e.target.value)} placeholder="https://instagram.com/yourhandle" />
            </div>
            <div>
              <label style={labelStyle}>YouTube URL</label>
              <input value={form.youtube} onChange={(e) => handleChange("youtube", e.target.value)} placeholder="https://youtube.com/@channel" />
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <label style={labelStyle}>Bio</label>
            <textarea
              value={form.bio}
              onChange={(e) => handleChange("bio", e.target.value)}
              placeholder="Share your riding story (max 160 characters)"
              rows={3}
              style={textareaStyle}
              maxLength={160}
            />
            <div style={helperStyle}>{form.bio.length}/160</div>
          </div>

          <div style={{ marginTop: 20 }}>
            <label style={labelStyle}>Bikes owned</label>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
              {form.bikes_owned.map((bike) => (
                <span key={bike} style={bikeChipStyle}>
                  {bike}
                  <button
                    type="button"
                    onClick={() => handleBikeRemove(bike)}
                    style={chipRemoveButtonStyle}
                    aria-label={`Remove ${bike}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              value={bikesInput}
              onChange={(e) => setBikesInput(e.target.value.slice(0, 40))}
              onKeyDown={handleBikesKeyDown}
              placeholder="Type bike name and press Enter"
            />
            <div style={helperStyle}>Add up to your favourite rides for other riders to see.</div>
          </div>

          <div style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div>
              <div style={labelStyle}>Profile visibility</div>
              <div style={helperStyle}>{form.is_public ? "Public - anyone can view" : "Private - only followers"}</div>
            </div>
            <button
              type="button"
              onClick={togglePrivacy}
              style={{
                ...toggleStyle,
                background: form.is_public ? "linear-gradient(90deg, var(--brand), #ff7a00)" : "var(--bg3)",
                justifyContent: form.is_public ? "flex-end" : "flex-start",
              }}
            >
              <span
                style={{
                  ...toggleKnobStyle,
                  background: form.is_public ? "#fff" : "rgba(255,255,255,0.12)",
                }}
              />
            </button>
          </div>

          {error ? <div style={errorStyle}>{error}</div> : null}
          {message ? <div style={successStyle}>{message}</div> : null}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 24 }}>
            <button type="submit" style={saveButtonStyle} disabled={saving}>
              {saving ? "Saving..." : "Save Profile"}
            </button>
            <Link to="/" style={secondaryButtonStyle}>Go to Home</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const sideCardStyle = {
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "20px",
  padding: "24px",
  boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
};

const avatarStyle = {
  width: 76,
  height: 76,
  borderRadius: "50%",
  background: "linear-gradient(135deg, var(--brand), #ff7a00)",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 24,
  fontWeight: 700,
  marginBottom: 18,
};

const progressWrapStyle = {
  background: "var(--bg3)",
  border: "1px solid var(--border)",
  borderRadius: "16px",
  padding: "14px",
};

const progressBarTrackStyle = {
  height: 8,
  background: "var(--bg4)",
  borderRadius: 999,
  overflow: "hidden",
};

const progressBarFillStyle = {
  height: "100%",
  background: "linear-gradient(90deg, var(--brand), #ff7a00)",
  borderRadius: 999,
};

const infoRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  background: "var(--bg3)",
  border: "1px solid var(--border)",
  borderRadius: "14px",
  padding: "12px 14px",
};

const infoLabelStyle = {
  color: "var(--text3)",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 1,
};

const homeLinkStyle = {
  display: "inline-block",
  marginTop: 20,
  textDecoration: "none",
  color: "var(--brand)",
  fontWeight: 700,
};

const formCardStyle = {
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "20px",
  padding: "24px",
  boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
};

const labelStyle = {
  display: "block",
  fontSize: 12,
  color: "var(--text2)",
  marginBottom: 8,
  textTransform: "uppercase",
  letterSpacing: 1,
};

const errorStyle = {
  marginTop: 18,
  color: "var(--danger)",
  fontSize: 13,
};

const helperStyle = {
  marginTop: 6,
  color: "var(--text3)",
  fontSize: 12,
};

const successStyle = {
  marginTop: 18,
  color: "var(--success)",
  fontSize: 13,
};

const saveButtonStyle = {
  background: "var(--brand)",
  color: "#fff",
  padding: "12px 18px",
  borderRadius: "var(--radius)",
  fontWeight: 700,
};

const secondaryButtonStyle = {
  display: "inline-block",
  textDecoration: "none",
  background: "var(--bg3)",
  color: "var(--text)",
  padding: "12px 18px",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
  fontWeight: 700,
};

const textareaStyle = {
  width: "100%",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  padding: "12px",
  background: "var(--bg3)",
  color: "var(--text)",
  fontFamily: "inherit",
  resize: "vertical",
  minHeight: "90px",
};

const bikeChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "rgba(232,76,30,0.12)",
  border: "1px solid rgba(232,76,30,0.35)",
  color: "var(--brand)",
  padding: "6px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
};

const chipRemoveButtonStyle = {
  background: "transparent",
  border: "none",
  color: "inherit",
  cursor: "pointer",
  fontSize: 12,
  lineHeight: 1,
  padding: 0,
};

const toggleStyle = {
  display: "inline-flex",
  alignItems: "center",
  width: 58,
  height: 30,
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,0.12)",
  padding: "4px",
  transition: "all 0.2s ease",
};

const toggleKnobStyle = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
  transition: "all 0.2s ease",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 12px",
  borderRadius: 999,
  background: "rgba(232,76,30,0.16)",
  color: "var(--brand)",
  fontSize: 12,
  fontWeight: 600,
};

const badgeOutlineStyle = {
  ...badgeStyle,
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.18)",
  color: "var(--text2)",
};

const profileBikeChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "var(--text)",
  padding: "6px 12px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 500,
};
